import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { TAG_PRESETS, TagChip } from "@/lib/tags";
import { ArticleBody } from "@/lib/articleBody";
import { Newspaper, Search, ArrowLeft, Calendar, User } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatDateShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function initialsOf(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
}

function excerpt(body = "", chars = 240) {
  // First paragraph, sans headings, capped.
  const paras = body.split(/\n\s*\n/).filter((p) => p && !p.startsWith("## "));
  const first = paras[0] || "";
  if (first.length <= chars) return first;
  return first.slice(0, chars).replace(/\s+\S*$/, "") + "…";
}

// ---------------- Detail view ----------------
function ArticleDetail({ id }) {
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${id}`)
      .then((r) => setArticle(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "Article not found" : "Failed to load article"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <div className="animate-pulse space-y-3">
          <div className="h-3 w-32 bg-white/10" />
          <div className="h-10 w-3/4 bg-white/10" />
          <div className="h-4 w-1/2 bg-white/10" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-14 text-center sm:px-6">
        <div className="mb-3 font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">Error</div>
        <div className="font-oswald text-2xl font-bold uppercase tracking-wider text-white">{error}</div>
        <Link
          to="/forecast"
          className="mt-6 inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
          data-testid="forecast-back-list"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Archive
        </Link>
      </div>
    );
  }


  return (
    <section className="relative py-10 sm:py-14" data-testid="forecast-article-detail">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Link
          to="/forecast"
          className="mb-6 inline-flex items-center gap-2 font-oswald text-[10px] uppercase tracking-[0.3em] text-gray-400 hover:text-white"
          data-testid="forecast-back-list"
        >
          <ArrowLeft className="h-3 w-3" /> Article Archive
        </Link>

        <article className="relative overflow-hidden rounded-sm border border-white/10 bg-gradient-to-br from-black via-[#0a111f] to-[#0b1424] p-8 sm:p-12">
          <div className="stripe-blue absolute inset-0 opacity-[0.05]" />
          <div className="relative">
            {article.image_url && (
              <div className="-mx-8 -mt-8 mb-7 sm:-mx-12 sm:-mt-12" data-testid="forecast-detail-image">
                <div className="relative h-60 w-full overflow-hidden sm:h-80">
                  <img src={article.image_url} alt={article.title} loading="lazy"
                       className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1424] via-transparent to-transparent" />
                </div>
              </div>
            )}
            <div className="font-oswald text-[10px] uppercase tracking-[0.4em] text-[#ffd60a]">
              {formatDate(article.published_at)}
            </div>
            <h1 className="mt-3 font-oswald text-3xl font-bold uppercase leading-tight tracking-wider text-white sm:text-4xl lg:text-5xl">
              {article.title}
            </h1>
            {article.subtitle && (
              <div className="mt-3 text-base text-gray-300 sm:text-lg">{article.subtitle}</div>
            )}
            {(article.tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {article.tags.map((t) => (
                  <TagChip key={t} tag={t} size="sm" />
                ))}
              </div>
            )}
            <div className="mt-5 h-[2px] w-16 bg-[#1f63c9]" />

            <div className="mt-6 space-y-4 text-sm leading-relaxed text-gray-200 sm:text-base">
              <ArticleBody body={article.body} />
            </div>

            {article.author && (
              <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1f63c9] font-oswald text-xs font-bold uppercase tracking-widest text-white">
                  {initialsOf(article.author)}
                </div>
                <div>
                  <div className="font-oswald text-sm font-bold uppercase tracking-wider text-white">
                    {article.author}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                    Chief Meteorologist · Georgia Weather Network
                  </div>
                </div>
              </div>
            )}
          </div>
        </article>
      </div>
    </section>
  );
}

// ---------------- List view ----------------
function ArticleList() {
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [activeTags, setActiveTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles?limit=50")
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  // All tags actually in use, sorted by count desc. Falls back to presets when empty.
  const availableTags = useMemo(() => {
    const counts = {};
    articles.forEach((a) => (a.tags || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    const used = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
    // Merge with the preset list so the row is always populated with the standard chips first
    const presetIds = TAG_PRESETS.map((p) => p.id);
    const extras = used.filter((t) => !presetIds.includes(t));
    return [...presetIds.filter((p) => used.includes(p) || !used.length), ...extras];
  }, [articles]);

  const toggleTag = (t) => {
    setActiveTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return articles.filter((a) => {
      // Tag filter (OR): article must have at least one selected tag.
      if (activeTags.length > 0 && !(a.tags || []).some((t) => activeTags.includes(t))) return false;
      if (!needle) return true;
      const hay = `${a.title || ""} ${a.subtitle || ""} ${a.author || ""} ${a.body || ""} ${(a.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [articles, q, activeTags]);

  return (
    <section className="relative py-10 sm:py-14" data-testid="forecast-archive">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">
              Forecast Discussion Archive
            </div>
            <h1 className="mt-1 font-oswald text-3xl font-bold uppercase tracking-wider text-white sm:text-4xl lg:text-5xl">
              All Forecasts
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-400">
              Every meteorologist's discussion ever published on GWN — from routine model chatter to severe weather playbooks. Search titles, subtitles, and body copy, or filter by tag.
            </p>
          </div>

          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="forecast-search"
              type="search"
              placeholder="Search HRRR, hurricane, tornado, ECMWF…"
              className="w-full rounded-sm border border-white/10 bg-black/60 pl-10 pr-3 py-2.5 text-sm text-white outline-none placeholder:text-gray-500 focus:border-[#1f63c9]"
            />
          </div>
        </div>

        {/* Tag filter bar */}
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="forecast-tag-filter">
          <div className="mr-1 font-oswald text-[10px] uppercase tracking-[0.3em] text-gray-500">Filter:</div>
          {availableTags.map((t) => (
            <TagChip
              key={t}
              tag={t}
              active={activeTags.includes(t)}
              onClick={() => toggleTag(t)}
            />
          ))}
          {activeTags.length > 0 && (
            <button
              onClick={() => setActiveTags([])}
              data-testid="forecast-tag-clear"
              className="ml-1 rounded-full border border-white/15 px-2 py-0.5 font-oswald text-[9px] uppercase tracking-[0.3em] text-gray-300 hover:border-[#1f63c9] hover:text-[#1f63c9]"
            >
              Clear
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-48 animate-pulse rounded-sm border border-white/10 bg-white/[0.02]" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded border border-dashed border-white/15 p-14 text-center">
            <Newspaper className="mx-auto h-10 w-10 text-gray-500" />
            <div className="mt-3 font-oswald text-lg uppercase tracking-widest text-white">
              {articles.length === 0 ? "No articles yet" : "No results"}
            </div>
            <p className="mt-1 text-sm text-gray-400">
              {articles.length === 0
                ? "Check back once Trey publishes his next discussion."
                : "Try a different keyword or clear the tag filter."}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-3 font-mono text-[11px] uppercase tracking-widest text-gray-500">
              {filtered.length} article{filtered.length === 1 ? "" : "s"}
              {q ? ` matching "${q}"` : ""}
              {activeTags.length > 0 ? ` · tagged ${activeTags.join(", ")}` : ""}
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <Link
                  key={a.id}
                  to={`/forecast/${a.id}`}
                  data-testid={`forecast-card-${a.id}`}
                  className="lift group relative overflow-hidden rounded-sm border border-white/10 bg-[#151b26] p-6 transition-colors hover:border-[#1f63c9]/60"
                >
                  {a.image_url && (
                    <div className="-mx-6 -mt-6 mb-4 h-36 overflow-hidden" data-testid={`forecast-card-image-${a.id}`}>
                      <img src={a.image_url} alt={a.title} loading="lazy"
                           className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#ffd60a]">
                    <Calendar className="h-3 w-3" />
                    {formatDateShort(a.published_at)}
                  </div>
                  <h2 className="mt-3 line-clamp-3 font-oswald text-xl font-bold uppercase leading-tight tracking-wider text-white group-hover:text-[#ffd60a]">
                    {a.title}
                  </h2>
                  {a.subtitle && (
                    <div className="mt-2 line-clamp-2 text-sm text-gray-400">{a.subtitle}</div>
                  )}
                  {(a.tags || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {a.tags.slice(0, 3).map((t) => (
                        <TagChip key={t} tag={t} size="xs" />
                      ))}
                    </div>
                  )}
                  <p className="mt-3 line-clamp-3 text-[13px] leading-relaxed text-gray-300">
                    {excerpt(a.body, 180)}
                  </p>
                  <div className="mt-4 flex items-center gap-2 border-t border-white/5 pt-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1f63c9] font-oswald text-[9px] font-bold uppercase tracking-widest text-white">
                      {initialsOf(a.author)}
                    </div>
                    <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      <User className="h-2.5 w-2.5" />
                      {a.author || "GWN Staff"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function ForecastArchive() {
  const { id } = useParams();
  return (
    <div className="relative min-h-screen">
      <Header />
      {id ? <ArticleDetail id={id} /> : <ArticleList />}
      <Footer />
    </div>
  );
}
